import { REGISTERED_STATIC_ROUTE_PATHS } from "./routeTokenRegistry";

const STORAGE_PATH_BY_TOKEN = "routePathByToken:";
const STORAGE_TOKEN_BY_PATH = "routeTokenByPath:";

/** Public paths stay readable in the address bar (login, subscription, etc.). */
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/update-password",
  "/update-api-key",
  "/websmpp/subscriber",
];

/** Prefix segment in the URL, e.g. /du/8k2m9x → dashboard. Override via VITE_ROUTE_TOKEN_PREFIX */
export function getRouteTokenPrefix() {
  const raw = import.meta.env.VITE_ROUTE_TOKEN_PREFIX;
  const segment =
    typeof raw === "string" && raw.trim() !== ""
      ? raw.trim().replace(/^\/+|\/+$/g, "")
      : "du";
  return `/${segment}`;
}

/** Router path segment (no slashes), e.g. "du" for path "du/:routeToken". */
export function getRouteTokenSegment() {
  return getRouteTokenPrefix().replace(/^\//, "");
}

export function isRouteObfuscationEnabled() {
  return import.meta.env.VITE_OBFUSCATE_ROUTES !== "false";
}

export function isPublicAppPath(pathname) {
  if (!pathname || pathname === "/") return true;
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function normalizePathInput(path) {
  if (!path || typeof path !== "string") return { pathname: "/", search: "" };
  const trimmed = path.trim();
  const qIndex = trimmed.indexOf("?");
  const hashIndex = trimmed.indexOf("#");
  const end = Math.min(
    qIndex === -1 ? trimmed.length : qIndex,
    hashIndex === -1 ? trimmed.length : hashIndex
  );
  let pathname = trimmed.slice(0, end);
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;
  pathname = pathname.replace(/\/{2,}/g, "/");
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }
  const search =
    qIndex === -1 ? "" : trimmed.slice(qIndex, hashIndex === -1 ? undefined : hashIndex);
  return { pathname, search };
}

function hashPathToToken(fullPath) {
  const salt = import.meta.env.VITE_ROUTE_TOKEN_SALT || "smpp-route-v1";
  const input = `${salt}:${fullPath}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0).toString(36).padStart(8, "0").slice(0, 8);
}

function rememberTokenMapping(token, fullPath) {
  try {
    localStorage.setItem(`${STORAGE_TOKEN_BY_PATH}${fullPath}`, token);
    localStorage.setItem(`${STORAGE_PATH_BY_TOKEN}${token}`, fullPath);
  } catch {
    /* quota / private mode */
  }
}

function lookupPathByToken(token) {
  try {
    return localStorage.getItem(`${STORAGE_PATH_BY_TOKEN}${token}`);
  } catch {
    return null;
  }
}

function lookupTokenByPath(fullPath) {
  try {
    return localStorage.getItem(`${STORAGE_TOKEN_BY_PATH}${fullPath}`);
  } catch {
    return null;
  }
}

function resolveTokenFromStaticRegistry(token) {
  for (const path of REGISTERED_STATIC_ROUTE_PATHS) {
    if (hashPathToToken(path) === token) {
      rememberTokenMapping(token, path);
      return path;
    }
  }
  return null;
}

/** Opaque short token for an internal path (not reversible from URL alone). */
export function pathToToken(path) {
  const { pathname, search } = normalizePathInput(path);
  const fullPath = `${pathname}${search}`;

  const cached = lookupTokenByPath(fullPath);
  if (cached) return cached;

  let token = hashPathToToken(fullPath);
  const existingPath = lookupPathByToken(token);
  if (existingPath && existingPath !== fullPath) {
    token = `${token}${Math.random().toString(36).slice(2, 5)}`;
  }

  rememberTokenMapping(token, fullPath);
  return token;
}

export function tokenToPath(token) {
  if (!token || typeof token !== "string") return null;
  const clean = token.trim().split("/")[0];
  const stored = lookupPathByToken(clean);
  if (stored) {
    const normalized = normalizePathInput(stored);
    const prefix = getRouteTokenPrefix();
    if (normalized.pathname.startsWith(`${prefix}/`)) {
      const innerToken = normalized.pathname.slice(prefix.length + 1).split("/")[0];
      if (innerToken === clean) return null;
      const inner = fromObfuscatedUrl(normalized.pathname);
      return inner ?? null;
    }
    return normalized;
  }
  const fromRegistry = resolveTokenFromStaticRegistry(clean);
  if (fromRegistry) return normalizePathInput(fromRegistry);
  return null;
}

export function toObfuscatedUrl(path) {
  if (!isRouteObfuscationEnabled()) return path;
  const { pathname, search } = normalizePathInput(path);
  if (isPublicAppPath(pathname)) return `${pathname}${search}`;
  const prefix = getRouteTokenPrefix();
  if (pathname.startsWith(`${prefix}/`)) return `${pathname}${search}`;
  const token = pathToToken(`${pathname}${search}`);
  return `${getRouteTokenPrefix()}/${token}`;
}

export function fromObfuscatedUrl(pathname) {
  const prefix = getRouteTokenPrefix();
  if (!pathname?.startsWith(`${prefix}/`)) return null;
  const token = pathname.slice(prefix.length + 1).split("/")[0];
  return tokenToPath(token);
}

export function getInternalPathFromLocation(location) {
  if (!isRouteObfuscationEnabled()) {
    return `${location.pathname}${location.search || ""}`;
  }
  const decoded = fromObfuscatedUrl(location.pathname);
  if (decoded) return `${decoded.pathname}${decoded.search || ""}`;
  return `${location.pathname}${location.search || ""}`;
}

/** True when the current (or given) location is under Reports, including obfuscated URLs. */
export function isReportsPath(location) {
  const loc =
    location && typeof location === "object" && "pathname" in location
      ? location
      : typeof window !== "undefined"
        ? window.location
        : { pathname: "/", search: "" };
  const internalPath = getInternalPathFromLocation(loc);
  return (
    internalPath.includes("/reports") || internalPath.includes("/report")
  );
}
